import {
    calculateEstimatedImpressionsRequest,
    getCampaignByIdRequest,
    getPurchasedCampaignDataFromCRM,
    getSearchLocationRequest
} from "@common";
import { createForm, fields, reactBinding } from "rd-redux-forms";
import { FETCH_STATE_INITIAL } from "rd-redux-http";
import { Country } from "../api";

/*
* Media plan
*/

export type CampaignGoal = "weekly" | "monthly" | "overall";
export type PriceModel = "CPC" | "CPM" | "vCPM";
export type Gender = "male" | "female" | "Unknown";
export type DeviceType = "ios" | "android";
export type Age = "13-17" | "18-24" | "25-34" | "35-54" | "55+" | "Unknown";
export type Restrictions = "No-gaming" | "No-dating" | "18+";

export interface CampaignMediaPlanFormFields {
    campaignName: string;
    startDate: string;
    endDate: string;
    priceModel: PriceModel;
    primaryKPI: string;
    secondaryKPI: string;
    interestTargeting: string;
    gender: Gender[];
    deviceType: DeviceType[];
    age: Age[];
    language: string;
    country: string;
    region: string;
    city: string;
    zipCode: string;
    timetable: any;
    restrictions: Restrictions[];
    adUnitFormat: string;
}

export const campaignMediaPlanForm = createForm<CampaignMediaPlanFormFields>("CAMPAIGN MEDIA PLAN FORM", {
    campaignName: fields.string(true),
    startDate: fields.string(true),
    endDate: fields.string(true),
    priceModel: fields.string(true) as any,
    primaryKPI: fields.string(true),
    secondaryKPI: fields.string(false),
    interestTargeting: fields.string(false),
    gender: fields.any(),
    deviceType: fields.any(),
    age: fields.any(),
    language: fields.string(false),
    country: fields.string(false),
    region: fields.string(false),
    city: fields.string(false),
    zipCode: fields.string(false),
    timetable: fields.any(),
    restrictions: fields.any(),
    adUnitFormat: fields.string(false)
});

export const campaignMediaPlanFormForEstimatedImpression = createForm<Partial<CampaignMediaPlanFormFields>>(
    "CAMPAIGN MEDIA PLAN FORM FOR ESTIMATED IMPRESSION",
    {
        startDate: fields.string(true),
        endDate: fields.string(true),
        language: fields.string(true),
        country: fields.string(true),
        adUnitFormat: fields.string(true),
        priceModel: fields.string(true) as any,
        interestTargeting: fields.string(false),
        gender: fields.any(),
        deviceType: fields.any(),
        age: fields.any(),
        region: fields.string(false),
        city: fields.string(false),
        zipCode: fields.string(false),
        timetable: fields.any(),
        restrictions: fields.any()
    }
);

export const campaignMediaPlanFormBindings = reactBinding()
    .default()
    .withForm(campaignMediaPlanForm);

/*
* Creatives plan
*/

export interface CampaignCreativesFormFields {
    adUnitType: string;
    adUnitId?: string;
    impressionTrackingUrl1: string;
    impressionTrackingUrl2: string;

    trackingTags: string;
}

export const campaignCreativesForm = createForm<CampaignCreativesFormFields>("CAMPAIGN CREATIVES FORM", {
    adUnitType: fields.string(false),
    adUnitId: fields.string(false),
    impressionTrackingUrl1: fields.string(false),
    impressionTrackingUrl2: fields.string(false),
    trackingTags: fields.string(false)
});

export const campaignCreativesFormBindings = reactBinding()
    .default()
    .withForm(campaignCreativesForm);
/*
* Budget form
*/

export interface CampaignBudgetFormFields {
    currency: string;
    budget: number;
}

export const campaignBudgetForm = createForm<CampaignBudgetFormFields>("CAMPAIGN BUDGET FORM", {
    currency: fields.string(true),
    budget: {
        ...fields.int(true),
        formatForEditing(value) {
            if ((value || "0") === "0") {
                return "";
            }

            return value;
        },
        formatForDisplay(value) {
            return value === null || value === undefined ? "0" : value.toFixed(0);
        }
    }
});

export const campaignBudgetFormBindings = reactBinding()
    .default()
    .withForm(campaignBudgetForm);

/*
*
*/

export interface CampaignState {
    campaignMediaPlanForm: typeof campaignMediaPlanForm.types.state;
    campaignCreativesForm: typeof campaignCreativesForm.types.state;
    campaignBudgetForm: typeof campaignBudgetForm.types.state;
    editingCampaign: typeof getCampaignByIdRequest.types.reduxState;
    summaryCampaign: typeof getPurchasedCampaignDataFromCRM.types.reduxState;
    estimatedImpression: typeof calculateEstimatedImpressionsRequest.types.reduxState;
    isSaveOrPurchaseProcessing: boolean;
    countryList: Country[];
    placeAndZipList: typeof getSearchLocationRequest.types.reduxState;
    campaignChangesFlag: boolean;
    updateNavStateFlag: boolean;
    updateCheckTimetableFlag: boolean;
    generatePdfOffer: null | "LOADING" | "LOADED" | "ERROR";
    isRequiredDataLoading: boolean;
}

export const defaultCampaignCreateState: CampaignState = {
    campaignMediaPlanForm: campaignMediaPlanForm.state.empty(),
    campaignCreativesForm: campaignCreativesForm.state.empty(),
    campaignBudgetForm: campaignBudgetForm.state.empty(),
    editingCampaign: { fetchState: FETCH_STATE_INITIAL },
    summaryCampaign: { fetchState: FETCH_STATE_INITIAL },
    estimatedImpression: { fetchState: FETCH_STATE_INITIAL },
    isSaveOrPurchaseProcessing: false,
    countryList: undefined,
    placeAndZipList: { fetchState: FETCH_STATE_INITIAL },
    campaignChangesFlag: false,
    updateNavStateFlag: false,
    updateCheckTimetableFlag: false,
    generatePdfOffer: null,
    isRequiredDataLoading: false
};
